import React, { useEffect, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import { ProductCard } from "../../components/productCard";
import { useProduce, useProductTypes } from "../../hooks/useProducts";
import {
  FaCartPlus,
  FaLocationArrow,
  FaShoppingBasket,
  FaTrash,
  FaMapMarkerAlt,
  FaChevronDown,
  FaLeaf,
} from "react-icons/fa";

import { FaWater, FaFire, FaWind, FaCheck } from "react-icons/fa";

import Logo from "../../assets/logo/new_logo_colored2.png";
import { slide as Menu } from "react-burger-menu";
import { useState } from "react";
import { Button } from "../../components/button";
import { useCart } from "../../subjects/CartContext";
import { thousands_separators } from "../../utils/formatCurrency";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { useForm } from "react-hook-form";
import { getCurrentWarehouse } from "../../utils/getCurrentWarehouse";
import {
  BranchSelectionModal,
  SELECTED_BRANCH_KEY,
} from "../../components/modal/branchModal";
import { EmptyStateComponent } from "../../components/modal/emptyState";
import {
  DiscountProductCard,
  RegularProductCard,
} from "../../components/productCard/cards";

const getProductImage = (name) => {
  let image;
  switch (true) {
    case name.toLowerCase().includes("tilapia"):
      image =
        "https://api.beta.markiti.laheri.co.ke/upload/2dda0c86-f866-11ed-aba4-b526e0ab41d1-Tilapia-001_copy.jpg";
      break;
    case name.toLowerCase().includes("nile"):
    case name.toLowerCase().includes("cat"):
      image = require("../../assets/pictures/NP-steak.jpeg");
      break;
    case name.toLowerCase().includes("fillet"):
      image = require("../../assets/pictures/Fillet.jpeg");
      break;
    case name.toLowerCase().includes("omena"):
      image = require("../../assets/pictures/omena.jpeg");
      break;
    case name.toLowerCase().includes("chicken"):
      image = require("../../assets/pictures/chicken.jpeg");
      break;
    default:
      image = require("../../assets/pictures/chicken.jpeg");
      break;
  }
  return image;
};

const ACCENT = "#494395";
const ACCENT_TINT = "#F6F5FC"; // selected-row wash
const ACCENT_BORDER = "#494395";
const TEAL = "#2AB4AA"; // reserved: "fresh / uncooked" only
const INK = "#241C40"; // primary text
const MUTED = "#8B86A3"; // secondary text
const LINE = "#E9E7F1"; // resting border
const SURFACE = "#FFFFFF";

const METHOD_STYLE = {
  deep_fried: {
    icon: FaWater,
    isNew: false,
    tagline: "Golden crust, tender centre",
    color: "#B8862F", // warm gold — crisp, oil-fried crust
  },
  grilled: {
    icon: FaFire,
    isNew: true,
    tagline: "Char-grilled, herb-marinated",
    color: "#BE5A34", // ember — smoke and char
  },
  none: {
    icon: FaLeaf,
    isNew: false,
    tagline: "No cooking — delivered fresh",
    color: TEAL,
  },
};

const CookingMethodSelector = ({
  methods,
  cookingMethodCosts,
  totalQty,
  quantitiesByMethod,
  onChange,
}) => {
  const cookMethods = methods || [];
  const assignedTotal = cookMethods.reduce(
    (sum, m) => sum + (quantitiesByMethod?.[m.code] || 0),
    0,
  );

  const hasSelection = assignedTotal > 0;

  const [expanded, setExpanded] = useState(true);

  if (!cookMethods.length) return null;

  const chosenMethods = cookMethods.filter(
    (m) => m.code !== "none" && quantitiesByMethod?.[m.code] > 0,
  );
  const freshQty = quantitiesByMethod?.none || 0;

  const summary =
    chosenMethods.length > 0
      ? chosenMethods
          .map((m) => `${quantitiesByMethod[m.code]} ${m.display_name}`)
          .join(" · ") + (freshQty > 0 ? ` · ${freshQty} Fresh` : "")
      : freshQty > 0
        ? "Set to Fresh — tap to choose a cooking style instead"
        : "Choose a cooking style — or select Fresh if you'd rather skip cooking";
  return (
    <Wrap>
      <Toggle type="button" onClick={() => setExpanded((e) => !e)}>
        <ToggleLeft>
          <Eyebrow>Cooking style</Eyebrow>
          <SummaryText $chosen={hasSelection}>
            {hasSelection && <FaCheck size={10} />}
            {summary}
          </SummaryText>
        </ToggleLeft>
        <Chevron $open={expanded} size={13} />
      </Toggle>

      {expanded && (
        <List>
          {cookMethods.map((method) => {
            const style = METHOD_STYLE[method.code] || METHOD_STYLE.deep_fried;
            const Icon = style.icon;
            const qty = quantitiesByMethod?.[method.code] || 0;
            const noneAvailable = quantitiesByMethod?.none || 0;
            const maxForThis =
              method.code === "none"
                ? totalQty - (assignedTotal - qty)
                : qty + noneAvailable;
            const active = qty > 0;
            const price = cookingMethodCosts[method.code] ?? method.price;

            return (
              <Row
                key={method.code}
                $active={active}
                role="group"
                aria-label={`${method.display_name}, ${qty} selected`}
              >
                <Badge $active={active} $color={style.color}>
                  <Icon size={15} />
                </Badge>

                <Info>
                  <NameLine>
                    <Name>{method.display_name}</Name>
                    {(method.isNew ?? style.isNew) && <NewTag>New</NewTag>}
                  </NameLine>
                  <Tagline>{method.tagline || style.tagline}</Tagline>
                </Info>

                <Right>
                  {price > 0 && <Price>+KSh {price}</Price>}
                  <Stepper>
                    <StepBtn
                      type="button"
                      disabled={qty <= 0}
                      aria-label={`Remove one ${method.display_name}`}
                      onClick={() => onChange(method.code, qty - 1)}
                    >
                      −
                    </StepBtn>
                    <StepValue $active={active}>{qty}</StepValue>
                    <StepBtn
                      type="button"
                      disabled={qty >= maxForThis}
                      aria-label={`Add one ${method.display_name}`}
                      onClick={() => onChange(method.code, qty + 1)}
                    >
                      +
                    </StepBtn>
                  </Stepper>
                </Right>

                {active && <SelectedMark aria-hidden="true" />}
              </Row>
            );
          })}
        </List>
      )}
    </Wrap>
  );
};

const Wrap = styled.div`
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid ${LINE};
`;

const Toggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: none;
  border: none;
  padding: 0.15rem 0;
  cursor: pointer;
  text-align: left;
`;

const ToggleLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const Eyebrow = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${MUTED};
`;

const SummaryText = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ $chosen }) => ($chosen ? INK : ACCENT)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled(FaChevronDown)`
  flex-shrink: 0;
  color: ${MUTED};
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "none")};
  transition: transform 0.18s ease;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.75rem;
`;

const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.85rem;
  border-radius: 0.7rem;
  border: 1px solid ${({ $active }) => ($active ? ACCENT_BORDER : LINE)};
  background: ${({ $active }) => ($active ? ACCENT_TINT : SURFACE)};
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
`;

const Badge = styled.div`
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active, $color }) => ($active ? $color : `${$color}1F`)};
  color: ${({ $active, $color }) => ($active ? "#FFFFFF" : $color)};
  transition:
    background 0.15s ease,
    color 0.15s ease;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
`;

const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Name = styled.span`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${INK};
  letter-spacing: 0.01em;
`;

const NewTag = styled.span`
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${ACCENT};
  border: 1px solid ${ACCENT};
  border-radius: 1rem;
  padding: 0.05rem 0.38rem;
`;

const Tagline = styled.span`
  font-size: 0.74rem;
  color: ${MUTED};
  line-height: 1.3;
`;

const Right = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
`;

const Price = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${MUTED};
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${ACCENT}33;
  border-radius: 1.2rem;
  padding: 2px;
  background: ${SURFACE};
`;

const StepBtn = styled.button`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: none;
  background: transparent;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1;
  color: ${({ disabled }) => (disabled ? "#D8D5E6" : ACCENT)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:hover:not(:disabled) {
    background: ${ACCENT}14;
  }
`;

const StepValue = styled.span`
  min-width: 1.2rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 700;
  color: ${({ $active }) => ($active ? INK : MUTED)};
`;

const SelectedMark = styled.div`
  position: absolute;
  top: -1px;
  left: -1px;
  bottom: -1px;
  width: 3px;
  border-radius: 0.7rem 0 0 0.7rem;
  background: ${ACCENT};
`;

const SummarySectionCard = styled.div`
  background: #f7f5ff;
  border-radius: 1rem;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
`;

const FeeLabel = styled.span`
  color: #7a7391;
  font-weight: 600;
`;

const FeeValue = styled.span`
  font-weight: 800;
  color: #241c40;
`;

const CheckoutButton = styled.button`
  width: 100%;
  background: #494395;
  border: none;
  border-radius: 0.65rem;
  padding: 1.15rem 1.5rem; /* Chunkier padding targets */
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.1s ease;

  &:hover {
    background: #3b357a;
  }
  &:active {
    transform: scale(0.99);
  }
`;

const CheckoutText = styled.span`
  font-size: 1.15rem; /* Scaled up from 1rem */
  font-weight: bold;
  color: #ffffff;
`;

const CheckoutTotalPrice = styled.span`
  font-size: 1.1rem; /* Scaled up from 0.95rem */
  font-weight: bold;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.18);
  padding: 0.4rem 1rem; /* Generous tags */
  border-radius: 0.5rem;
`;

const isFryEligible = (name) => name?.toLowerCase().includes("tilapia");
const CLIENT_COOKING_METHODS = {
  tilapia: ["deep_fried", "grilled", "none"],
  chicken: ["grilled", "none"],
};

const getMethodsForProduct = (product, cookingMethodCosts) => {
  const nameLower = product?.display_name?.toLowerCase() || "";
  const key = Object.keys(CLIENT_COOKING_METHODS).find((k) =>
    nameLower.includes(k),
  );
  if (!key) return [];
  const LABELS = {
    deep_fried: {
      display_name: "Deep Fried",
      tagline: "Our original crispy classic",
    },
    grilled: {
      display_name: "Grilled/Airfried",
      tagline: "Smoky, herb-marinated, char-grilled",
    },
    none: {
      display_name: "Fresh",
      tagline: "No cooking — delivered fresh, no extra charge",
    },
  };
  return CLIENT_COOKING_METHODS[key].map((code) => ({
    code,
    display_name: LABELS[code].display_name,
    tagline: LABELS[code].tagline,
    price: cookingMethodCosts[code] || 0,
  }));
};

var styles = {
  bmBurgerButton: {
    position: "fixed",
    width: "36px",
    height: "30px",
    right: "36px",
    top: "15px",
    zIndex: 1000,
  },
  bmBurgerBars: {
    background: "#fff",
  },
};

const Products = () => {
  const [selectedFilter, setSelectedFilter] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const { data: products } = useProduce(selectedFilter);
  const { data: product_types } = useProductTypes();
  const {
    items,
    getTotalPrice,
    removeItem,
    getItemsCount,
    setCookingMethodQuantity,
    cookingMethodCosts,
    getCookingFee,
  } = useCart();

  const history = useHistory();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    try {
      const savedBranch = localStorage.getItem(SELECTED_BRANCH_KEY);
      return savedBranch ? JSON.parse(savedBranch) : null;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  });
  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  // Function to handle branch selection from the modal
  const handleSelectBranch = (branch) => {
    setSelectedBranch(branch);
  };
  const handleFilterChange = (id) => {
    const selectedType = product_types?.find((t) => t.id === id);
    const slug = selectedType
      ? selectedType.name.toLowerCase().replace(/\s+/g, "-")
      : "";

    setSelectedFilter(id);
    history.push({
      pathname: history.location.pathname,
      search: slug ? `?product_type=${slug}` : "",
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const categorySlug = params.get("product_type");

    if (categorySlug && product_types?.length > 0) {
      const matchedType = product_types.find(
        (t) => t.name.toLowerCase().replace(/\s+/g, "-") === categorySlug,
      );
      if (matchedType) {
        setSelectedFilter(matchedType.id);
      }
    } else if (!categorySlug) {
      setSelectedFilter("");
    }
  }, [product_types, history.location.search]);
  const groupedCartItems = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.product.id;
      if (!map.has(key)) {
        map.set(key, {
          product: item.product,
          totalQty: 0,
          quantitiesByMethod: {},
          totalPrice: 0,
        });
      }
      const group = map.get(key);
      group.totalQty += item.qty;
      const methodKey = item.cooking_method || "none";
      group.quantitiesByMethod[methodKey] =
        (group.quantitiesByMethod[methodKey] || 0) + item.qty;

      group.totalPrice += item.discountedTotal || item.totalPrice;
    });
    return Array.from(map.values());
  }, [items]);

  return (
    <>
      <Container>
        <NavbarContainer>
          <LogoContainer onClick={() => window.location.replace("/")}>
            <LogoImg src={Logo} />
          </LogoContainer>
          <LocationContainer>
            <BranchContainer onClick={openModal}>
              <FaMapMarkerAlt size={24} color="#494395" />
              <div>
                <p>{selectedBranch?.name}</p>
                <strong>{selectedBranch?.code}</strong>
              </div>
            </BranchContainer>
            <BrandContainer onClick={() => setIsOpen(true)}>
              <FaCartPlus size={24} color="#494395" />
              <span>{getItemsCount()}</span>
            </BrandContainer>
          </LocationContainer>
        </NavbarContainer>
        <Header>
          <Title>{/* Our <span>Products</span> */}</Title>
        </Header>

        <ProductsContainer>
          <div>
            <strong>Filters</strong>
            <FiltersContainers>
              <Filter
                active={selectedFilter === ""}
                onClick={() => handleFilterChange("")}
              >
                All
              </Filter>
              {product_types
                ?.filter((type) => type.id !== 4)
                ?.sort((a, b) => {
                  const priorityIds = [1, 2, 5, 9, 8, 7, 3];

                  const indexA = priorityIds.indexOf(a.id);
                  const indexB = priorityIds.indexOf(b.id);

                  const posA = indexA === -1 ? 99 : indexA;
                  const posB = indexB === -1 ? 99 : indexB;

                  return posA - posB;
                })
                ?.map((type) => (
                  <Tippy content={type?.description} key={type.id}>
                    <Filter
                      active={selectedFilter === type.id} // Use handleFilterChange here
                      onClick={() => handleFilterChange(type.id)}
                    >
                      {type.name}
                    </Filter>
                  </Tippy>
                ))}
            </FiltersContainers>
          </div>

          {products?.length > 0 ? (
            <ProductList>
              {products
                ?.filter(
                  (prod) =>
                    prod.id !== "66d580f5-6b81-49a3-bde0-7305c7c407fe" &&
                    prod.id !== "2d1c4fe1-4be1-4f36-bfb5-159a42deb61e" &&
                    !prod?.name.includes("Byproducts"),
                )
                ?.map((item) => {
                  return item?.discount_amount ? (
                    <DiscountProductCard
                      product={item}
                      key={item.id}
                      description={item?.description}
                      title={item.name}
                      price={item.unit_value}
                      backgroundColor="white"
                      qty_available={item?.quantity_available_for_sale}
                      itemInCart={items?.find(
                        (prod) => prod?.product.id === item.id,
                      )}
                      discounted_price={item?.discounted_price}
                      discount_amount={item?.discount_amount}
                      // imgUrl={getProductImage(item.display_name)}
                      imgUrl={
                        item?.product_images?.length
                          ? item?.product_images[0]?.url
                          : getProductImage(item.display_name)
                      }
                    />
                  ) : (
                    <RegularProductCard
                      product={item}
                      key={item.id}
                      description={item?.description}
                      title={item.name}
                      price={item.unit_value}
                      backgroundColor="white"
                      qty_available={item?.quantity_available_for_sale}
                      itemInCart={items?.find(
                        (prod) => prod?.product.id === item.id,
                      )}
                      discounted_price={item?.discounted_price}
                      discount_amount={item?.discount_amount}
                      // imgUrl={getProductImage(item.display_name)}
                      imgUrl={
                        item?.product_images?.length
                          ? item?.product_images[0]?.url
                          : getProductImage(item.display_name)
                      }
                    />
                  );
                })}
            </ProductList>
          ) : (
            <EmptyStateComponent />
          )}
        </ProductsContainer>
      </Container>
      <BurgerMenu
        styles={styles}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        isOpen={isOpen}
        outerContainerId={"outer-container"}
        customBurgerIcon={false}
        right
      >
        <CartDrawerWrapper>
          {/* Header */}
          <MenuHeader>
            <FaShoppingBasket size={24} color="#747577" />
            <HeaderTitle>{getItemsCount()} items</HeaderTitle>
          </MenuHeader>

          {/* Scrollable Item Container */}
          <CartItemsScrollArea>
            {groupedCartItems?.map((group) => {
              const fryEligible = isFryEligible(group.product?.display_name);
              const resolvedMethods =
                group.product?.cooking_methods?.length > 0
                  ? group.product.cooking_methods
                  : getMethodsForProduct(group.product, cookingMethodCosts);
              const lineTotal = group.product?.discounted_price
                ? group.totalQty * group.product.discounted_price
                : group.totalPrice;

              return (
                <CartItem key={group.product?.id}>
                  <CartItemTop>
                    <CartItemName>{group.product?.display_name}</CartItemName>
                    <DeleteButton
                      onClick={() => removeItem(group.product?.id)}
                      aria-label="Remove product"
                    >
                      <FaTrash size={14} />
                    </DeleteButton>
                  </CartItemTop>

                  <CartItemMeta>
                    <MetaText>
                      {group.totalQty} × Ksh.{" "}
                      {thousands_separators(group.product?.unit_value)}
                    </MetaText>
                    <CartItemPrice>
                      Ksh. {thousands_separators(lineTotal?.toFixed(0))}
                    </CartItemPrice>
                  </CartItemMeta>
                  {resolvedMethods.length > 0 && (
                    <CookingMethodSelector
                      methods={resolvedMethods}
                      cookingMethodCosts={cookingMethodCosts}
                      totalQty={group.totalQty}
                      quantitiesByMethod={group.quantitiesByMethod}
                      onChange={(code, qty) =>
                        setCookingMethodQuantity(group.product.id, code, qty)
                      }
                    />
                  )}
                </CartItem>
              );
            })}
          </CartItemsScrollArea>

          {/* Summary & Checkout Stick to Bottom */}
          <CartBottomStickySection>
            {(getCookingFee() > 0 ||
              (items?.length && getTotalPrice() < 4000)) && (
              <SummarySectionCard>
                {getCookingFee() > 0 && (
                  <FeeRow>
                    <FeeLabel>Service Fee</FeeLabel>
                    <FeeValue>
                      Ksh. {thousands_separators(getCookingFee())}
                    </FeeValue>
                  </FeeRow>
                )}
                {items?.length && getTotalPrice() < 4000 && (
                  <FeeRow>
                    <FeeLabel>Delivery Fee</FeeLabel>
                    <FeeValue>Ksh. 400</FeeValue>
                  </FeeRow>
                )}
              </SummarySectionCard>
            )}

            <CheckOutActions>
              <CheckoutButton onClick={() => history.push("/checkout")}>
                <CheckoutText>Checkout Now</CheckoutText>
                <CheckoutTotalPrice>
                  Ksh. {thousands_separators(getTotalPrice())}
                </CheckoutTotalPrice>
              </CheckoutButton>
            </CheckOutActions>
          </CartBottomStickySection>
        </CartDrawerWrapper>
      </BurgerMenu>

      <BranchSelectionModal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        onSelectBranch={handleSelectBranch}
      />
    </>
  );
};

const Container = styled.div`
  display: grid;
  background-color: #f6f9fc;
  padding-bottom: 1rem;
`;

const BranchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0rem 1rem;
  cursor: pointer;
  div {
    p {
      font-weight: bold;
      color: #494395;
      padding: 0rem;
      margin: 0rem;
    }
    strong {
      font-weight: bold;
      padding: 0rem;
      margin: 0rem;
    }
  }
  @media (max-width: 600px) {
    display: none;
  }
`;

const LocationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0rem 1rem;
  cursor: pointer;
`;
const FiltersContainers = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-content: space-evenly;
  align-items: center;
  margin: 1rem 0rem;
  column-gap: 1rem;
  row-gap: 1rem;
  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 11.5rem);
    column-gap: 1rem;
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(9, 8.5rem);
    column-gap: 1rem;
  }
`;

const Filter = styled.div`
  padding: 0.5rem;
  background-color: ${(props) => (props?.active ? "#494395" : "white")};
  color: ${(props) => (props?.active ? "white" : "#494395")};
  border-radius: 0.5rem;
  font-weight: bold;
  cursor: pointer;
  text-align: center;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 5rem;
`;

const Title = styled.h3`
  font-size: 3.5rem;

  span {
    color: #494395;
  }
  @media screen and (max-width: 670px) {
    font-size: 1.5rem;
  }
`;

const ProductsContainer = styled.div`
  margin: 0 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  row-gap: 1rem;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 1rem;
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
    column-gap: 1rem;
  }
`;
const NavbarContainer = styled.div`
  width: 100%;
  padding: 0.5rem 2em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  position: fixed;
  z-index: 10;
  color: black;
`;

const BrandContainer = styled.div`
  cursor: pointer;
  position: relative;
  border-radius: 5px;
  border: 1px solid #494395;
  background-color: rgba(73, 67, 149, 0.1);
  padding: 0.5rem;
  span {
    background-color: red;
    color: white;
    height: 1rem;
    width: 1rem;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: -10px;
    right: -5px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  cursor: pointer;
  flex-direction: ${({ inline }) => (inline ? "row" : "column")};
  align-items: center;
  @media screen and (max-width: 670px) {
    margin-top: 0.5rem;
  }
`;

const LogoImg = styled.img`
  width: 200px;
  height: 70px;

  @media screen and (max-width: 670px) {
    width: 100px;
    height: 40px;
  }

  ${({ inline }) =>
    inline &&
    css`
      width: 24px;
      height: 24px;
      margin-right: 6px;
    `};

  ${({ small }) =>
    small &&
    css`
      width: 8em;
      height: 8em;
    `};
`;

// Unified flex grid block ensuring absolute layout containment inside the drawer
const CartDrawerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff; /* Clean white background */
  box-sizing: border-box;
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem 1.25rem; /* Increased padding to expand header zone */
  border-bottom: 2px solid #f1f5f9; /* Defined clean border separation */
`;

const HeaderTitle = styled.p`
  margin: 0 0 0 1rem;
  font-size: 1.4rem; /* Scaled up from 1.15rem */
  font-weight: bold;
  color: #4a5568 !important; /* Richer shade for prominent header text */
`;

const CartItemsScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 0; /* Extra top/bottom spacing for items */

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

const CartItem = styled.div`
  background: #f8fafc; /* Light structural card surface */
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  padding: 1.25rem; /* Expanded padding inside each product row */
  margin: 0 1.25rem 1rem; /* Scaled horizontal and vertical tracking margins */
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
`;

const CartItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
`;

const CartItemName = styled.p`
  font-size: 1.1rem; /* Scaled up from 0.9rem to beautifully fill layout space */
  font-weight: bold;
  color: #1a202c;
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 0.35rem; /* Larger tap target */
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    color 0.15s ease,
    transform 0.15s ease;

  &:hover {
    color: #ef4444;
    transform: scale(1.1);
  }

  svg {
    width: 16px; /* Scaled up delete icon frame */
    height: 16px;
  }
`;

const CartItemMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.85rem; /* Generous separation below the title */
`;

const MetaText = styled.span`
  font-size: 0.95rem; /* Scaled up from 0.8rem */
  color: #64748b;
  font-weight: 500;
`;

const CartItemPrice = styled.span`
  font-size: 1.1rem; /* Scaled up from 0.9rem */
  font-weight: 800; /* Extra bold highlight for item total pricing */
  color: #1a202c;
`;

/* Sticky Bottom Drawer Configurations */
const CartBottomStickySection = styled.div`
  background: #ffffff;
  border-top: 2px solid #f1f5f9;
  padding: 1.5rem 1.25rem; /* Roomier spacing dock */
  margin-top: auto;
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.03);
`;

const CheckOutActions = styled.div`
  width: 100%;
`;

/* Reverted Burger Menu Width Styles to Original Parameters */
const BurgerMenu = styled(Menu)`
  width: 25% !important; /* Restored original 25% configuration */
  height: 100%;
  position: absolute;
  top: 0;

  .bm-menu {
    z-index: 10;
    background: #ffffff; /* Clean white sidebar canvas */
    box-shadow: -1px 0px 5px 1px #aaaaaa;
    height: 100%;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .bm-cross {
    background: #747577;
  }
  .bm-overlay {
    background: rgba(0, 0, 0, 0.3) !important;
    top: 0;
    left: 0;
  }
  .bm-item-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding-top: 0;
  }

  @media screen and (max-width: 670px) {
    width: 100% !important; /* Desktop-to-mobile width override constraint protection */
    height: 100% !important;
  }
`;

export default Products;
